import type {
  AckPayload,
  MessageType,
  PayloadOf,
  StandardPayload,
  TypedMessage,
} from '@navstation-portal/iframe/data-model/message-type';
import { HANDSHAKE_TIMEOUT_MS } from '@navstation-portal/iframe/data-model/message-type';
import { extractIframeId } from '@navstation-portal/iframe/util/iframe-query-params';
import { createMessage, isMessageEvent } from '@navstation-portal/iframe/util/message-type';
import { RemoteState, initialRemoteState } from './remote-state';
import { isWhitelisted } from './whitelist';

export interface RemoteCommunicationConfig {
  whitelist: string[];
  isDev?: boolean;
}

export type RemoteStateListener = (state: Readonly<RemoteState>) => void;

export class RemoteCommunication {
  #state: RemoteState = { ...initialRemoteState };
  #listeners = new Set<RemoteStateListener>();
  #timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  #messageHandler: (event: MessageEvent) => void;
  #config: Required<RemoteCommunicationConfig>;
  #titleObserver: MutationObserver | null = null;
  #lastSentTitle: string | null = null;

  constructor(config: RemoteCommunicationConfig) {
    this.#config = { isDev: false, ...config };
    this.#messageHandler = (event: MessageEvent) => this.#handleWindowMessage(event);
    window.addEventListener('message', this.#messageHandler);
  }

  getState(): Readonly<RemoteState> {
    return { ...this.#state };
  }

  subscribe(listener: RemoteStateListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  init(): void {
    if (window === window.parent) {
      this.#patch({ state: 'disconnected', error: 'Not running in iframe' });
      return;
    }

    const iframeId = extractIframeId();
    if (!iframeId) {
      this.#patch({ state: 'failed', error: 'No iframeId in URL' });
      return;
    }

    this.#patch({ iframeId, state: 'connecting', error: null });

    window.parent.postMessage(createMessage(iframeId, 'ready', undefined), '*');

    this.#observeTitle();

    this.#timeoutHandle = setTimeout(() => {
      if (this.#state.state === 'connecting') {
        this.#patch({ state: 'failed', error: 'Connection timeout - no response from parent' });
      }
    }, HANDSHAKE_TIMEOUT_MS);
  }

  send(payload: StandardPayload): void {
    this.#sendTyped('standard', payload);
  }

  #sendTyped<T extends MessageType>(type: T, payload: PayloadOf<T>): void {
    const { state, parentOrigin, iframeId } = this.#state;

    if (state !== 'connected') {
      this.#patch({ error: 'Cannot send - not connected' });
      return;
    }
    if (!parentOrigin) {
      this.#patch({ error: 'Cannot send - no parent origin' });
      return;
    }
    if (!iframeId) {
      this.#patch({ error: 'Cannot send - no iframeId' });
      return;
    }

    window.parent.postMessage(createMessage(iframeId, type, payload), parentOrigin);
  }

  destroy(): void {
    window.removeEventListener('message', this.#messageHandler);
    if (this.#timeoutHandle !== null) {
      clearTimeout(this.#timeoutHandle);
      this.#timeoutHandle = null;
    }
    this.#titleObserver?.disconnect();
    this.#titleObserver = null;
    this.#listeners.clear();
  }

  #observeTitle(): void {
    if (this.#titleObserver) return;
    const target = document.querySelector('title');
    if (!target) return;
    this.#titleObserver = new MutationObserver(() => this.#maybeSendTitle());
    this.#titleObserver.observe(target, { childList: true, characterData: true, subtree: true });
  }

  #maybeSendTitle(): void {
    if (this.#state.state !== 'connected') return;
    const title = document.title;
    if (title === this.#lastSentTitle) return;
    this.#lastSentTitle = title;
    this.#sendTyped('title', { title });
  }

  #patch(partial: Partial<RemoteState>): void {
    this.#state = { ...this.#state, ...partial };
    this.#listeners.forEach((listener) => listener(this.#state));
  }

  #handleWindowMessage(event: MessageEvent): void {
    if (!isMessageEvent(event)) return;

    if (!isWhitelisted(event.origin, this.#config.whitelist, this.#config.isDev)) return;

    const message = event.data;
    const { origin } = event;

    if (message.type === 'config') {
      this.#handleConfig(message as TypedMessage<'config'>, origin);
      return;
    }

    if (message.type === 'settings') {
      this.#handleSettings(message as TypedMessage<'settings'>, origin);
      return;
    }

    if (message.type === 'standard') {
      this.#handleStandardMessage(message as TypedMessage<'standard'>, origin);
      return;
    }

    this.#patch({ error: `Unknown message type: ${message.type}` });
  }

  #handleConfig(message: TypedMessage<'config'>, origin: string): void {
    const { iframeId: currentIframeId } = this.#state;

    if (!currentIframeId) {
      this.#patch({ state: 'failed', error: 'No iframeId available' });
      return;
    }

    if (message.iframeId !== currentIframeId) {
      this.#patch({ state: 'failed', error: 'iframeId mismatch' });
      return;
    }

    if (!isWhitelisted(origin, this.#config.whitelist, this.#config.isDev)) {
      this.#patch({ state: 'failed', error: 'Invalid origin' });
      return;
    }

    const ack: AckPayload = { success: true };
    window.parent.postMessage(createMessage(currentIframeId, 'ack', ack), origin);

    if (this.#timeoutHandle !== null) {
      clearTimeout(this.#timeoutHandle);
      this.#timeoutHandle = null;
    }

    const { headless, ...settings } = message.payload;
    this.#patch({
      state: 'connected',
      parentOrigin: origin,
      headless: headless ?? false,
      settings,
      error: null,
    });

    this.#maybeSendTitle();
  }

  #handleStandardMessage(message: TypedMessage<'standard'>, origin: string): void {
    const { parentOrigin } = this.#state;

    if (parentOrigin && origin !== parentOrigin) {
      this.#patch({ error: `Invalid origin: ${origin} (expected: ${parentOrigin})` });
      return;
    }

    this.#patch({ messages: [...this.#state.messages, message] });
  }

  #handleSettings(message: TypedMessage<'settings'>, origin: string): void {
    const { parentOrigin, state } = this.#state;

    if (parentOrigin && origin !== parentOrigin) return;
    if (state !== 'connected') return;

    this.#patch({ settings: { ...this.#state.settings, ...message.payload } });
  }
}
